import type {
  BillingCreatePurchaseResponse,
  BillingInvoicesResponse,
  BillingPurchaseHistoryResponse,
  BillingPurchaseResponse,
  BillingSummaryResponse,
  BillingTopupOptionsResponse,
  ConfirmSetupIntentResponse,
  RateCardResponse,
  SetupIntentResponse,
  SignupConfigResponse,
  UserOrganizationResponse,
  WalletStatusResponse,
} from "@server/types/responseTypes";
import { BILLING_REQUEST_TIMEOUT_MS } from "@/constants/billing";
import { apiGet, createApiUrl, createOrgApiUrl } from "@/utils/apiUtils";
import { ApiRequestError, fetchWithAuth } from "@/utils/fetch";

/** Stripe-backed self-serve pricing catalog rendered by the account NUX. */
export type RateCard = RateCardResponse["data"];

/** One metered product rate from the self-serve pricing catalog. */
export type RateCardRate = RateCard["rates"][number];

/**
 * Requests the server-authoritative self-serve eligibility payload with an
 * authenticated GET and no request body. The read has no mutation side effects.
 *
 * @returns The `self_serve_signup_enabled` payload from `/api/billing/signup-config`.
 * @throws Propagates token, network, timeout, non-success response, and parsing
 * failures after the shared authenticated-fetch wrapper logs them.
 */
export const fetchSignupConfig = async () => {
  const res = await apiGet<SignupConfigResponse>(
    createApiUrl("billing", "signup-config"),
    { timeout: BILLING_REQUEST_TIMEOUT_MS },
  );
  return res.data;
};

/**
 * Fetches `GET /api/billing/rate-card` and returns its public pricing catalog.
 * Authentication and catalog availability failures propagate.
 *
 * @returns The public self-serve rate-card catalog from the server response.
 * @throws Propagates token, network, timeout, non-success response, and parsing
 * failures from the shared authenticated-fetch wrapper.
 */
export const fetchRateCard = async () => {
  const res = await apiGet<RateCardResponse>(
    createApiUrl("billing", "rate-card"),
    { timeout: BILLING_REQUEST_TIMEOUT_MS },
  );
  return res.data;
};

// Billing mutations use preserveErrorBody: the 402 contracts
// (payment_declined / payment_method_required) drive the payment step's UI.
const postBilling = async <T>(
  url: string,
  data?: Record<string, unknown>,
  headers?: Record<string, string>,
) => {
  return fetchWithAuth<T>(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: data ? JSON.stringify(data) : undefined,
    preserveErrorBody: true,
    timeout: BILLING_REQUEST_TIMEOUT_MS,
  });
};

/**
 * Creates or resumes the organization-bound SetupIntent used to collect a card.
 *
 * @param organizationId - Internal organization identifier.
 * @param formKey - Stable UUID for this organization payment form and retries.
 * @returns Browser-safe intent identity, client secret, and current provider state.
 */
export const createSetupIntent = async (
  organizationId: string,
  formKey: string,
) => {
  const res = await postBilling<SetupIntentResponse>(
    createOrgApiUrl(organizationId, "billing", "setup-intent"),
    undefined,
    { "Idempotency-Key": formKey },
  );
  return res.data;
};

/**
 * Verifies the organization owns a completed SetupIntent and saves its payment
 * method as the Stripe invoice default.
 *
 * @param organizationId - Internal organization identifier.
 * @param setupIntentId - Server-issued Stripe SetupIntent identifier.
 * @returns The server confirmation status.
 */
export const confirmSetupIntent = async (
  organizationId: string,
  setupIntentId: string,
) => {
  const res = await postBilling<ConfirmSetupIntentResponse>(
    createOrgApiUrl(organizationId, "billing", "setup-intent", "confirm"),
    { setup_intent_id: setupIntentId },
  );
  return res.data;
};

/**
 * Returns fresh wallet and unexpired invoice-default-card state for NUX resume routing.
 *
 * @param organizationId - Internal organization identifier.
 */
export const fetchWalletStatus = async (organizationId: string) => {
  const res = await apiGet<WalletStatusResponse>(
    createOrgApiUrl(organizationId, "billing", "wallet"),
    { timeout: BILLING_REQUEST_TIMEOUT_MS },
  );
  return res.data;
};

/**
 * Activates a pending self-serve organization after its saved invoice-default card is unexpired.
 *
 * @param organizationId - Internal organization identifier.
 * @returns The activated organization used to refresh local application state.
 */
export const activateOrganization = async (organizationId: string) => {
  const res = await postBilling<UserOrganizationResponse>(
    createOrgApiUrl(organizationId, "billing", "activate"),
  );
  return res.data;
};

/** Reads the selected organization's local credit projection; never mutates funds. */
export const fetchBillingSummary = async (organizationId: string) =>
  (
    await fetchWithAuth<BillingSummaryResponse>(
      createOrgApiUrl(organizationId, "billing", "summary"),
      { timeout: BILLING_REQUEST_TIMEOUT_MS, preserveErrorBody: true },
    )
  ).data;

/** Fetches the authoritative catalog and capability for new purchases. */
export const fetchTopupOptions = async (organizationId: string) =>
  (
    await fetchWithAuth<BillingTopupOptionsResponse>(
      createOrgApiUrl(organizationId, "billing", "top-up-options"),
      { timeout: BILLING_REQUEST_TIMEOUT_MS, preserveErrorBody: true },
    )
  ).data;

/** Creates or replays a purchase. The caller must persist the offer and UUID before requesting. */
export const createBillingPurchase = async (
  organizationId: string,
  offerId: string,
  formKey: string,
) =>
  (
    await postBilling<BillingCreatePurchaseResponse>(
      createOrgApiUrl(organizationId, "billing", "purchases"),
      { offer_id: offerId },
      { "Idempotency-Key": formKey },
    )
  ).data;

/** Reads an owned purchase without invoking Checkout or fulfillment. */
export const fetchBillingPurchase = async (
  organizationId: string,
  purchaseId: string,
) =>
  (
    await fetchWithAuth<BillingPurchaseResponse>(
      createOrgApiUrl(
        organizationId,
        "billing",
        "purchases",
        encodeURIComponent(purchaseId),
      ),
      { timeout: BILLING_REQUEST_TIMEOUT_MS, preserveErrorBody: true },
    )
  ).data;

const billingPageUrl = (
  organizationId: string,
  kind: string,
  cursor: string | null,
) => {
  const query = new URLSearchParams({ limit: "20" });
  if (cursor) query.set("cursor", cursor);
  return `${createOrgApiUrl(organizationId, "billing", kind)}?${query}`;
};

/** Reads one organization-bound purchase-history page with an opaque cursor. */
export const fetchBillingPurchases = async (
  organizationId: string,
  cursor: string | null = null,
) =>
  (
    await fetchWithAuth<BillingPurchaseHistoryResponse>(
      billingPageUrl(organizationId, "purchases", cursor),
      { timeout: BILLING_REQUEST_TIMEOUT_MS, preserveErrorBody: true },
    )
  ).data;

/** Reads invoices independently of credit-account readiness or top-up capability. */
export const fetchBillingInvoices = async (
  organizationId: string,
  cursor: string | null = null,
) =>
  (
    await fetchWithAuth<BillingInvoicesResponse>(
      billingPageUrl(organizationId, "invoices", cursor),
      { timeout: BILLING_REQUEST_TIMEOUT_MS, preserveErrorBody: true },
    )
  ).data;

/** Identifies server authorization denial independently of provider errors or message wording. */
export const isBillingForbidden = (error: unknown): boolean =>
  error instanceof ApiRequestError && error.status === 403;
